import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import DashboardInstallCard from '../components/DashboardInstallCard';
import { usePWAReconnect } from '../hooks/usePWAReconnect';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ──────────────────────────────────────────────
   Extract qrId from a scanned value or pasted URL
   ────────────────────────────────────────────── */
function extractQrId(value) {
  if (!value) return null;
  const v = value.trim();
  // Direct qrId format: qr_XXXXX (allow underscores, hyphens, alphanumeric)
  if (/^qr_[A-Za-z0-9_-]+$/.test(v)) return v;
  // URL containing /tip/qr_XXXXX
  const m = v.match(/\/tip\/(qr_[A-Za-z0-9_-]+)/);
  if (m) return m[1];
  return null;
}

/* ──────────────────────────────────────────────
   Scan to Tip — camera scanner + manual fallback
   ────────────────────────────────────────────── */
function ScanToTip({ navigate }) {
  const [scanning, setScanning] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [scanError, setScanError] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const scannerRef = useRef(null);
  const readerRef = useRef(null);

  const stopScanner = useCallback(async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      }
    } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  }, []);

  const startScanner = async () => {
    setScanError(null);
    setLastScanned(null);
    setScanning(true);

    // Small delay so the reader div renders
    await new Promise((r) => setTimeout(r, 100));

    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          setLastScanned(decodedText);
          const qrId = extractQrId(decodedText);
          if (qrId) {
            stopScanner();
            navigate(`/tip/${qrId}`);
          } else {
            setScanError(`QR detected but not a tip link: ${decodedText.slice(0, 60)}`);
          }
        },
        () => {} // ignore failures per frame
      );
    } catch {
      // Camera denied or unavailable → fall back to manual
      stopScanner();
      setShowManual(true);
      setScanError('Camera unavailable — enter a tip link below');
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const qrId = extractQrId(manualValue);
    if (qrId) navigate(`/tip/${qrId}`);
    else setScanError('Invalid tip link. Use format: tipme.space/tip/qr_XXXXX');
  };

  // Cleanup on unmount
  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  return (
    <div className="mb-5" data-testid="scan-to-tip-section">
      {/* Scanner viewport */}
      {scanning && (
        <div className="rounded-xl overflow-hidden mb-3" style={{ backgroundColor: '#000' }}>
          <div id="qr-reader" ref={readerRef} style={{ width: '100%' }} />
          <button onClick={stopScanner}
            className="w-full text-sm py-2 font-medium"
            style={{ backgroundColor: '#333', color: '#e8e3db' }}
            data-testid="stop-scanner-btn">
            Cancel
          </button>
        </div>
      )}

      {/* Primary CTA */}
      {!scanning && (
        <button onClick={startScanner}
          className="w-full flex items-center justify-center gap-3 text-base font-semibold py-4 rounded-xl transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#f0b90b', color: '#1a1a1a' }}
          data-testid="scan-qr-btn">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
          </svg>
          Scan QR to Tip
        </button>
      )}

      {/* Error message */}
      {scanError && <p className="text-xs mt-2 text-center" style={{ color: '#f87171' }}>{scanError}</p>}

      {/* Manual entry toggle */}
      {!scanning && !showManual && (
        <button onClick={() => { setShowManual(true); setScanError(null); }}
          className="w-full text-center text-sm mt-2 hover:underline"
          style={{ color: '#666' }}
          data-testid="manual-entry-toggle">
          Enter tip link manually
        </button>
      )}

      {/* Manual entry input */}
      {showManual && (
        <form onSubmit={handleManualSubmit} className="mt-3 flex gap-2" data-testid="manual-entry-form">
          <input
            type="text"
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            placeholder="tipme.space/tip/qr_XXXXX"
            className="flex-1 text-sm px-3 py-2.5 rounded-xl border outline-none focus:border-yellow-500"
            style={{ backgroundColor: '#2a2a2a', borderColor: '#444', color: '#e8e3db' }}
            data-testid="manual-entry-input"
          />
          <button type="submit"
            className="text-sm px-4 py-2.5 rounded-xl font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#f0b90b', color: '#1a1a1a' }}
            data-testid="manual-entry-submit">
            Go
          </button>
        </form>
      )}

      {/* Divider */}
      <div className="mt-5 border-t" style={{ borderColor: '#333' }} />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Tip Summary Card
   ────────────────────────────────────────────── */
function TipSummary({ data }) {
  const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

  return (
    <div className="grid grid-cols-2 gap-3 mb-5" data-testid="tip-summary">
      <div className="rounded-xl p-4" style={{ backgroundColor: '#2a2a2a' }}>
        <p className="text-xs mb-1" style={{ color: '#888' }}>Total Tips</p>
        <p className="text-xl font-bold" style={{ color: '#f0b90b' }} data-testid="total-tips">
          {fmt(data.totalTips)}
        </p>
      </div>
      <div className="rounded-xl p-4" style={{ backgroundColor: '#2a2a2a' }}>
        <p className="text-xs mb-1" style={{ color: '#888' }}>Total Received</p>
        <p className="text-xl font-bold" style={{ color: '#e8e3db' }} data-testid="total-amount">
          {fmt(data.totalAmount)} TIP
        </p>
      </div>
      <div className="rounded-xl p-4" style={{ backgroundColor: '#2a2a2a' }}>
        <p className="text-xs mb-1" style={{ color: '#888' }}>Today</p>
        <p className="text-xl font-bold" style={{ color: '#e8e3db' }} data-testid="today-tips">
          {data.todayTips}
        </p>
      </div>
      <div className="rounded-xl p-4" style={{ backgroundColor: '#2a2a2a' }}>
        <p className="text-xs mb-1" style={{ color: '#888' }}>Unique Tippers</p>
        <p className="text-xl font-bold" style={{ color: '#e8e3db' }} data-testid="unique-tippers">
          {data.uniqueTippers}
        </p>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Recent Tips List
   ────────────────────────────────────────────── */
function RecentTips({ tips }) {
  if (!tips || tips.length === 0) return null;

  const shortAddr = (a) => (a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '???');
  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="rounded-xl overflow-hidden mb-5" style={{ backgroundColor: '#2a2a2a' }} data-testid="recent-tips">
      <div className="px-4 py-3 border-b" style={{ borderColor: '#333' }}>
        <p className="text-sm font-medium" style={{ color: '#e8e3db' }}>Recent Tips</p>
      </div>
      {tips.map((t, i) => (
        <div key={t.txHash || i}
          className="flex items-center justify-between px-4 py-3 border-b last:border-b-0"
          style={{ borderColor: '#333' }}
          data-testid={`recent-tip-${i}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: t.source === 'qr' ? 'rgba(240,185,11,0.2)' : 'rgba(99,102,241,0.2)', color: t.source === 'qr' ? '#f0b90b' : '#818cf8' }}>
              {t.source === 'qr' ? 'QR' : 'SM'}
            </div>
            <div>
              <p className="text-sm" style={{ color: '#e8e3db' }}>{shortAddr(t.sender)}</p>
              <p className="text-xs" style={{ color: '#666' }}>{timeAgo(t.datetime)}</p>
            </div>
          </div>
          <p className="text-sm font-medium" style={{ color: '#f0b90b' }}>+{t.amount} TIP</p>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Notification Toggle (mock — localStorage)
   ────────────────────────────────────────────── */
function NotifyToggle() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem('tipme_notify') === 'true');

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem('tipme_notify', String(next));
  };

  return (
    <button onClick={toggle}
      className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border transition-colors hover:opacity-80"
      style={{ borderColor: enabled ? '#f0b90b' : '#444', color: enabled ? '#f0b90b' : '#e8e3db' }}
      data-testid="notify-toggle">
      <svg className="w-4 h-4" fill={enabled ? '#f0b90b' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
      {enabled ? 'Notifications On' : 'Notify Me'}
    </button>
  );
}

/* ──────────────────────────────────────────────
   Main Dashboard Page
   ────────────────────────────────────────────── */
export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { isReconnecting, isPWA, connectWithFlag } = usePWAReconnect(openConnectModal);

  const [qrData, setQrData] = useState(null);
  const [tipData, setTipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Only show test controls when ?test=true
  const showTestControls = searchParams.get('test') === 'true';

  /* ── Fetch QR + Tips in parallel ── */
  const fetchData = useCallback(async () => {
    if (!address) return;
    setLoading(true);
    setError(null);
    try {
      const [qrRes, tipRes] = await Promise.all([
        axios.get(`${API_URL}/api/qr/wallet/${address}`).catch(() => ({ data: { exists: false } })),
        axios.get(`${API_URL}/api/tips/received/${address}`).catch(() => ({ data: null })),
      ]);
      setQrData(qrRes.data);
      setTipData(tipRes.data);
    } catch (err) {
      console.error('[Dashboard] fetch failed:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    if (isConnected && address) fetchData();
    else { setQrData(null); setTipData(null); }
  }, [isConnected, address, fetchData]);

  /* ── Helpers ── */
  const qrUrl = qrData?.qrId ? `${window.location.origin}/tip/${qrData.qrId}` : null;
  const tipLink = address ? `${window.location.origin}/tip/${qrData?.qrId || address}` : null;

  const copyLink = async () => {
    if (!qrUrl) return;
    try { await navigator.clipboard.writeText(qrUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* noop */ }
  };

  const downloadQR = () => {
    if (!qrData?.qrId) return;
    const svg = document.getElementById('dashboard-qr-svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    canvas.width = 512; canvas.height = 512;
    img.onload = () => { ctx.fillStyle = 'white'; ctx.fillRect(0, 0, 512, 512); ctx.drawImage(img, 0, 0, 512, 512); const link = document.createElement('a'); link.download = `tipme-qr-${qrData.qrId}.png`; link.href = canvas.toDataURL('image/png'); link.click(); };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const shareLink = async () => {
    const url = tipLink || qrUrl;
    if (!url) return;
    if (navigator.share) {
      try { await navigator.share({ title: 'TipMe', text: 'Send me a tip!', url }); }
      catch (err) { if (err.name !== 'AbortError') copyLink(); }
    } else {
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      catch { /* noop */ }
    }
  };

  /* ────────────────────────────────────────────
     RENDER
     ──────────────────────────────────────────── */
  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: '#1a1a1a' }} data-testid="dashboard-page">
      <div className="max-w-md mx-auto">

        <h1 className="text-2xl font-bold mb-6" style={{ color: '#e8e3db' }}>My Dashboard</h1>

        {/* ── PWA Reconnecting ── */}
        {!isConnected && isReconnecting && (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: '#2a2a2a' }} data-testid="pwa-reconnecting">
            <svg className="w-10 h-10 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24" style={{ color: '#f0b90b' }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="font-medium mb-1" style={{ color: '#e8e3db' }}>Finishing connection...</p>
            <p className="text-sm" style={{ color: '#888' }}>Restoring your wallet session</p>
          </div>
        )}

        {/* ── Wallet not connected ── */}
        {!isConnected && !isReconnecting && (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: '#2a2a2a' }} data-testid="connect-wallet-cta">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(240,185,11,0.2)' }}>
              <svg className="w-7 h-7" fill="none" stroke="#f0b90b" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
              </svg>
            </div>
            <p className="font-medium mb-2" style={{ color: '#e8e3db' }}>Connect your wallet</p>
            <p className="text-sm mb-4" style={{ color: '#888' }}>to access your dashboard</p>
            <div className="flex justify-center">
              {isPWA ? (
                <button
                  onClick={() => connectWithFlag(openConnectModal)}
                  className="px-6 py-2.5 rounded-xl font-medium transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#f0b90b', color: '#1a1a1a' }}
                  data-testid="pwa-connect-btn"
                >
                  Connect Wallet
                </button>
              ) : (
                <ConnectButton />
              )}
            </div>
          </div>
        )}

        {/* ── Loading ── */}
        {isConnected && loading && (
          <div className="rounded-xl p-6 flex items-center justify-center gap-3" style={{ backgroundColor: '#2a2a2a' }} data-testid="dashboard-loading">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: '#f0b90b' }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm" style={{ color: '#888' }}>Loading dashboard...</span>
          </div>
        )}

        {/* ── Error ── */}
        {isConnected && error && !loading && (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: '#2a2a2a' }} data-testid="dashboard-error">
            <p className="text-sm text-red-400">{error}</p>
            <button onClick={fetchData} className="mt-3 text-sm hover:underline" style={{ color: '#f0b90b' }}>Try again</button>
          </div>
        )}

        {/* ── Dashboard content ── */}
        {isConnected && !loading && !error && (
          <>
            {/* 1. Scan to Tip — primary CTA */}
            <ScanToTip navigate={navigate} />

            {/* 1.5 PWA Install card (shows on 2nd+ visit) */}
            <DashboardInstallCard />

            {/* 2. Tip Summary stats grid */}
            {tipData && <TipSummary data={tipData} />}

            {/* Action bar: Share + Notify */}
            <div className="flex gap-2 mb-5" data-testid="action-bar">
              <button onClick={shareLink}
                className="flex-1 flex items-center justify-center gap-2 text-sm px-3 py-2.5 rounded-xl font-medium transition-colors hover:opacity-90"
                style={{ backgroundColor: '#f0b90b', color: '#1a1a1a' }}
                data-testid="share-tip-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                </svg>
                Share my tip link
              </button>
              <NotifyToggle />
            </div>

            {/* Recent Tips */}
            {tipData && <RecentTips tips={tipData.recentTips} />}

            {/* QR Section — No QR yet */}
            {!qrData?.exists && (
              <div className="rounded-xl p-5" style={{ backgroundColor: '#2a2a2a' }} data-testid="create-qr-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(240,185,11,0.2)' }}>
                      <svg className="w-5 h-5" fill="none" stroke="#f0b90b" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#e8e3db' }}>Get Your QR Code</p>
                      <p className="text-xs" style={{ color: '#888' }}>Receive tips in person</p>
                    </div>
                  </div>
                  <button onClick={() => navigate('/register-qr')}
                    className="text-sm px-4 py-2 rounded-xl font-medium text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: '#f0b90b' }}
                    data-testid="create-qr-btn">
                    Create QR
                  </button>
                </div>
              </div>
            )}

            {/* QR Section — QR exists */}
            {qrData?.exists && (
              <div className="rounded-xl p-5" style={{ backgroundColor: '#2a2a2a' }} data-testid="qr-display-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f0b90b' }}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#e8e3db' }}>My QR Code</p>
                    <p className="text-xs" style={{ color: '#888' }}>Share it anywhere</p>
                  </div>
                </div>
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-4 rounded-xl">
                    <QRCodeSVG id="dashboard-qr-svg" value={qrUrl} size={160} level="H" includeMargin={true} />
                  </div>
                </div>
                {qrData.label && <p className="text-center text-sm mb-3" style={{ color: '#888' }}>{qrData.label}</p>}
                <div className="flex gap-2 justify-center">
                  <button onClick={copyLink}
                    className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border transition-colors hover:opacity-80"
                    style={{ borderColor: '#444', color: '#e8e3db' }}
                    data-testid="copy-qr-link">
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button onClick={downloadQR}
                    className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border transition-colors hover:opacity-80"
                    style={{ borderColor: '#444', color: '#e8e3db' }}
                    data-testid="download-qr">
                    Download
                  </button>
                  <button onClick={shareLink}
                    className="flex items-center gap-2 text-sm px-3 py-2 rounded-xl border transition-colors hover:opacity-80"
                    style={{ borderColor: '#444', color: '#e8e3db' }}
                    data-testid="share-qr">
                    Share
                  </button>
                </div>
              </div>
            )}

            {/* Test controls — hidden unless ?test=true */}
            {showTestControls && (
              <div className="mt-6 p-4 rounded-xl border border-dashed" style={{ borderColor: '#555', backgroundColor: '#222' }} data-testid="test-controls">
                <p className="text-xs mb-2" style={{ color: '#888' }}>Test Controls (dev only)</p>
                <button onClick={fetchData} className="text-xs px-3 py-1 rounded border mr-2" style={{ borderColor: '#555', color: '#888' }}>Refresh</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
